import { forwardRef, Ref } from 'react';

import { Shadow } from '@opendoor/tokens-studio/dist/bricks-next/types/Novo';
import { GetProps, styled, TamaguiElement, View } from '@tamagui/core';

import { WithoutPressableProps } from '../../helpers/a11yprops';
import { AnalyticsProps, ComponentWithViewTracking } from '../../helpers/analytics';
import { getShadowProps } from '../../helpers/tokens';

// default styling for View: https://github.com/tamagui/tamagui/blob/1ded484e5e47fd05a1a49b4dd8e315c0baaa678d/packages/web/src/constants/constants.ts#L1-L20
// flex direction defaults to column rather than row
const BaseBox = styled(View, {
  name: 'Box',
  tag: 'div',
  variants: {
    shadow: (shadowToken: Shadow) => {
      return getShadowProps(shadowToken);
    },
  } as const,
});

type BaseBoxProps = GetProps<typeof BaseBox>;

export type BoxProps = WithoutPressableProps<BaseBoxProps> &
  Partial<AnalyticsProps> & {
    /**
     * Threshold for impression tracking. 0.5 means 50% of the box must be in view to trigger impression
     * Default: 1 (100%)
     */
    inViewThreshold?: number;
    /**
     * This adds shadow styling to the Box (works for mobile & web)
     */
    shadow?: Shadow;
  };

/**
 * Box is an abstract styled component that renders a `<div>` element by default but can be updated to render something like `<span>` through the tag prop.
 * Some important things to note:
 * - Box defaults to being a flexBox with `flexDirection` set to 'column' for both web and mobile. This comes from Tamagui and is intended to ensure
 *   consistency across platforms. Tamagui also adds defaults for other flex props like `flexShrink` and `flexGrow` for the same reason. If you're taking
 *   a mobile-first approach, these defaults will mostly make sense, but they should be overridden as needed. Remember that you can also use media query props
 *   like `$largerThanSM` to set prop values for specific screen sizes.
 * - For a11y reasons, Box does not support press handlers like `onPress`. If you need a box that can be pressed and act like either a button or a link, use
 *   PressableBox instead.
 */
export const Box = forwardRef(function Box(
  { analyticsName, trackImpression = false, inViewThreshold, ...props }: BoxProps,
  ref: Ref<TamaguiElement>,
) {
  const BaseBoxWithProps = <BaseBox ref={ref} {...props} />;

  if (analyticsName && trackImpression) {
    return (
      <ComponentWithViewTracking
        Component={BaseBoxWithProps}
        analyticsName={analyticsName}
        eventName={'boxViewEvent'}
        inViewThreshold={inViewThreshold}
        ref={ref}
      />
    );
  }

  return BaseBoxWithProps;
});
